import { Button } from '@shared/components/ui/button';
import { Search } from '@shared/components/ui/Search/Search';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ShippingFiltersProps {
  search: string;
  country: string;
  courier: string;
  service: string;
  onSearchChange: (value: string) => void;
  onCountryChange: (value: string) => void;
  onCourierChange: (value: string) => void;
  onServiceChange: (value: string) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  availableCountries: string[];
  availableCouriers: string[];
  availableServices: string[];
}

export const ShippingFilters = ({
  search,
  country,
  courier,
  service,
  onSearchChange,
  onCountryChange,
  onCourierChange,
  onServiceChange,
  onClearFilters,
  hasActiveFilters,
  availableCountries,
  availableCouriers,
  availableServices,
}: ShippingFiltersProps) => {
  const { t } = useTranslation('ratesModule');
  const [localSearch, setLocalSearch] = useState(search);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalSearch(newValue);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        onSearchChange(newValue.trim());
      }, 300);
    },
    [onSearchChange]
  );

  const handleSearchSubmit = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    onSearchChange(localSearch.trim());
  }, [localSearch, onSearchChange]);

  const handleClearFilters = useCallback(() => {
    setLocalSearch('');
    onClearFilters();
  }, [onClearFilters]);

  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <Search
        placeholder={t('filters.shipping.searchPlaceholder')}
        value={localSearch}
        onChange={handleSearchInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="shipping-rates-search"
        inputDataTestId="shipping-rates-search-input"
      />

      <Select value={country} onValueChange={onCountryChange}>
        <SelectTrigger className="w-48">
          <SelectValue placeholder={t('filters.shipping.allCountries')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('filters.shipping.allCountries')}</SelectItem>
          {availableCountries.map((countryName) => (
            <SelectItem key={countryName} value={countryName}>
              {countryName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={courier} onValueChange={onCourierChange}>
        <SelectTrigger className="w-48">
          <SelectValue placeholder={t('filters.shipping.allCouriers')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('filters.shipping.allCouriers')}</SelectItem>
          {availableCouriers.map((courierName) => (
            <SelectItem key={courierName} value={courierName}>
              {courierName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={service} onValueChange={onServiceChange}>
        <SelectTrigger className="w-48">
          <SelectValue placeholder={t('filters.shipping.allServices')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('filters.shipping.allServices')}</SelectItem>
          {availableServices.map((serviceName) => (
            <SelectItem key={serviceName} value={serviceName}>
              {serviceName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {hasActiveFilters && (
        <Button variant="outline" size="sm" onClick={handleClearFilters}>
          {t('filters.clear')}
        </Button>
      )}
    </div>
  );
};

